import { Injectable, NgZone, effect, inject, signal, untracked } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom, Subject } from 'rxjs';
import { signMessage } from '@wagmi/core';
import { AppkitService } from './appkit.service';
import { LocalStorageService } from './local-storage.service';
import { ToastrService } from './toastr.service';
import { WalletSessionSecurityService } from './wallet-session-security.service';
import { getCsrfToken } from './csrf-token.util';
import { config } from '../../configs/wagmi-config';
import { environment } from '../../../../environments/environment';
import { PendingEvent, JoinResumeContext, AuthState, ConnectResponse, AuthFlow } from './wallet-interfaces';

@Injectable({
  providedIn: 'root',
})
export class WalletApiIntegrteService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private appkitService = inject(AppkitService);
  private localStorageService = inject(LocalStorageService);
  private toastr = inject(ToastrService);
  private walletSessionSecurity = inject(WalletSessionSecurityService);
  private ngZone = inject(NgZone);

  private pendingRoute = '/app';
  private pendingEvent: PendingEvent | null = null;
  readonly joinResumeContext = signal<JoinResumeContext | null>(null);
  private authInFlight = false;
  private flowSessionId = 0;
  private handledSessionId = -1;
  private readonly requestCancel$ = new Subject<void>();

  /** Set to true by any component that opens a full-screen overlay/popup (e.g., event detail popup). */
  readonly anyOverlayOpen = signal<boolean>(false);

  readonly state = signal<AuthState>({
    open: false,
    loading: false,
    flow: null,
    walletAddress: '',
    username: '',
    email: '',
    title: 'Connect wallet',
    message: 'Connect your wallet to continue.',
  });

  constructor() {
    effect(() => {
      const wallet = this.appkitService.getWalletSignal()();
      const current = untracked(() => this.state());

      if (wallet?.connected && wallet.account && current.flow && !current.loading && !this.authInFlight && !this.isAuthenticatedSession(wallet.account)) {
        void this.handleConnectedWallet(wallet.account);
      }

      void this.validateWalletSession(wallet?.account ?? null, wallet?.connected ?? false);
    });
  }

  private withCsrfHeader(headers: HttpHeaders = new HttpHeaders()): HttpHeaders {
    const csrfToken = getCsrfToken();
    return csrfToken ? headers.set('x-xsrf-token', csrfToken) : headers;
  }

  private getCurrentRequestUrl(): string {
    return typeof window !== 'undefined' ? window.location.href : '';
  }

  private getLocalTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  }

  /**
   * Checks if the current session is valid for performing authenticated actions.
   * Validates that the connected wallet matches the authenticated wallet and that a token exists.
   * @returns true if the session is valid, false otherwise.
   */
  isValidAuthenticatedSession(): boolean {
    const token = this.localStorageService.getToken();
    const connectedWallet = this.appkitService.getWalletSignal()()?.account ?? null;
    const authenticatedWallet = this.localStorageService.getAuthenticatedWallet();
    if (!token || !connectedWallet || !authenticatedWallet) {
      return false;
    }
    return authenticatedWallet.toLowerCase() === connectedWallet.toLowerCase();
  }

  private isAuthenticatedSession(walletAddress?: string): boolean {
    const token = this.localStorageService.getToken();
    const authenticatedWallet = this.localStorageService.getAuthenticatedWallet();
    if (!token || !walletAddress || !authenticatedWallet) {
      return false;
    }
    return authenticatedWallet.toLowerCase() === walletAddress.toLowerCase();
  }

  /**
   * Force session invalidation due to wallet change or other reasons.
   * Clears all relevant local storage and resets the auth state.
   * @param message Optional message to display to the user.
   */
  async forceSessionInvalidation(message = 'Wallet changed. Please reconnect and sign again.') {
    this.requestCancel$.next();
    this.state.update((current) => ({
      ...current,
      open: false,
      loading: false,
      flow: null,
      walletAddress: '',
      username: '',
      email: '',
      title: 'Connect wallet',
      message: 'Connect your wallet to continue.',
    }));
    this.pendingEvent = null;
    this.joinResumeContext.set(null);
    this.authInFlight = false;
    this.flowSessionId += 1;
    this.handledSessionId = -1;
    await this.walletSessionSecurity.forceSessionInvalidation(message);
  }

  /**
   * Invalidates the current session and redirects the user.  
   * This is useful for scenarios where the wallet has changed or the session is no longer valid. 
   * @param message Optional message to display to the user upon redirection.
   */
  async invalidateSessionAndRedirect(message = 'Wallet changed. Please reconnect and sign again.') {
    await this.forceSessionInvalidation(message);
  }

  /**
   *  Validates the current wallet session against the authenticated wallet and token.
   * If the session is invalid, it forces session invalidation.
   * @param connectedWallet The currently connected wallet address.
   * @param isConnected Whether the wallet is currently connected.
   * @return Promise that resolves to true if the session is valid, false otherwise.
   */
  private async validateWalletSession(connectedWallet: string | null, isConnected: boolean) {
    const authenticatedWallet = this.localStorageService.getAuthenticatedWallet();
    const hasSession = !!this.localStorageService.getToken();

    if (!hasSession) {
      return;
    }

    // CRITICAL: DO NOT REVERT THIS LOGIC.
    // If you revert this to include `!isConnected`, the app will permanently delete the user's session every time the page refreshes!
    // This is the EXACT reason it keeps asking for a signature.
    if (isConnected && connectedWallet && authenticatedWallet && connectedWallet.toLowerCase() !== authenticatedWallet.toLowerCase()) {
      await this.forceSessionInvalidation();
    }
  }

  startFlow(flow: AuthFlow, pendingRoute = '', pendingEvent: PendingEvent | null = null) {
    this.pendingRoute = pendingRoute;
    this.pendingEvent = pendingEvent;

    const wallet = this.appkitService.getWalletSignal()();
    const authenticatedSession = this.isAuthenticatedSession(wallet?.account);

    if (flow === 'connect' && wallet?.connected && authenticatedSession) {
      if (this.pendingRoute) {
        void this.navigateToPendingRoute();
      }
      return;
    }

    this.flowSessionId += 1;
    this.handledSessionId = -1;
    this.state.update((current) => ({
      ...current,
      open: false,
      loading: false,
      flow,
      title: flow === 'connect' ? 'Connect wallet' : 'Join event',
      message:
        flow === 'connect'
          ? 'Connect your wallet to continue.'
          : 'Connect your wallet to join this event.',
    }));


    if (flow === 'join' && authenticatedSession && wallet?.connected && pendingEvent?.eventId) {
      this.state.update((current) => ({ ...current, open: false, loading: false, flow: null }));
      void this.joinEvent(pendingEvent);
      return;
    }

    if (flow === 'connect' && wallet?.connected) {
      void this.handleConnectedWallet(wallet.account);
      return;
    }

    if (!wallet?.connected) {
      this.appkitService.openConnectModal();
      return;
    }

    if (wallet.account && flow !== 'connect') {
      void this.handleConnectedWallet(wallet.account);
    }
  }

  closeModal() {
    this.state.update((current) => ({
      ...current,
      open: false,
      loading: false,
      username: '',
      email: '',
      flow: null,
    }));
  }

  /**
   * Cancel the signup flow for a new user.
   * Disconnects the wallet so the user is fully reset, then closes the modal.
   * Only call this when the modal is in a NEW-USER signup state (flow is active
   * and it opened the sign-up form rather than login).
   */
  async cancelSignup() {
    this.closeModal();
    this.flowSessionId += 1;   // invalidate any in-flight auth attempts
    this.authInFlight = false;
    await this.appkitService.disconnectWalletOnly();
  }

  updateSignupField(field: 'username' | 'email', value: string) {
    this.state.update((current) => ({ ...current, [field]: value }));
  }

  canSubmitSignup(): boolean {
    const state = this.state();
    return !!state.walletAddress && !!state.username.trim() && !!state.email.trim() && !state.loading;
  }

  async submitSignup() {
    const state = this.state();
    if (!state.walletAddress || !state.username.trim() || !state.email.trim() || state.loading) return;

    this.authInFlight = true;
    this.state.update((current) => ({ ...current, loading: true }));

    try {
      const { message, token } = await this.requestNonce(state.walletAddress);
      const wagmiConfig = this.appkitService.fetchConfig();
      const signature = await signMessage(wagmiConfig, { message });

      await firstValueFrom(
        this.http.post(
          `${environment.API_BASE_URL}auth/signup`,
          {
            signature,
            walletAddress: state.walletAddress,
            username: state.username.trim(),
            email: state.email.trim(),
          },
          {
            headers: this.withCsrfHeader(new HttpHeaders({
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            }))
          }
        )
      );

      this.markAuthenticatedSession(token);
      this.localStorageService.setAuthenticatedWallet(state.walletAddress);
      this.toastr.showToast('Authentication completed successfully.');
      const pendingEvent = this.pendingEvent;
      this.closeModal();

      if (pendingEvent) {
        this.joinResumeContext.set({
          event: pendingEvent,
          needsJoinConfirmation: true,
        });
        return;
      }

      await this.navigateToPendingRoute();
    } catch (error) {
      console.error('Signup flow failed:', error);
      this.toastr.showToast('Signup failed. Please try again.', 'error');
      this.state.update((current) => ({ ...current, loading: false }));
    } finally {
      this.authInFlight = false;
    }
  }

  private async handleConnectedWallet(walletAddress: string) {
    if (this.isAuthenticatedSession(walletAddress)) {
      this.state.update((current) => ({ ...current, open: false, loading: false, flow: null }));
      if (this.pendingRoute) {
        void this.navigateToPendingRoute();
      }
      return;
    }

    const state = this.state();
    if (!state.flow || state.loading || this.authInFlight) return;
    const currentSessionId = this.flowSessionId;
    if (this.handledSessionId === currentSessionId) return;
    this.handledSessionId = currentSessionId;

    this.authInFlight = true;
    this.state.update((current) => ({ ...current, loading: true, walletAddress }));

    try {
      const connectResponse = await this.requestConnect(walletAddress);

      if (this.isVerifiedConnectResponse(connectResponse)) {
        await this.verifyExistingWallet(walletAddress);
        return;
      }

      this.state.update((current) => ({
        ...current,
        loading: false,
        open: true,
        walletAddress,
        title: 'Create your account',
        message: 'This wallet is new here. Please finish signup to continue.',
      }));
    } catch (error) {
      console.error('Wallet auth failed:', error);
      this.toastr.showToast('Wallet authentication failed.', 'error');
      this.state.update((current) => ({ ...current, loading: false }));
    } finally {
      this.authInFlight = false;
    }
  }

  private async verifyExistingWallet(walletAddress: string) {
    const { message, token } = await this.requestNonce(walletAddress);
    const wagmiConfig = this.appkitService.fetchConfig();
    const signature = await signMessage(wagmiConfig, { message });

    await firstValueFrom(
      this.http.post(
        `${environment.API_BASE_URL}auth/login`,
        { signature },
        {
          headers: this.withCsrfHeader(new HttpHeaders({
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          }))
        }
      )
    );

    this.markAuthenticatedSession(token);
    this.localStorageService.setAuthenticatedWallet(walletAddress);
    this.state.update((current) => ({ ...current, loading: false }));
    const pendingEvent = this.pendingEvent;
    this.closeModal();

    if (pendingEvent) {
      this.joinResumeContext.set({
        event: pendingEvent,
        needsJoinConfirmation: true,
      });
      return;
    }

    await this.navigateToPendingRoute();
  }

  async joinEvent(event: PendingEvent): Promise<boolean | 'already_joined'> {
    if (!event?.eventId) {
      this.toastr.showToast('Event details are missing. Please try again.', 'error');
      return false;
    }

    const isValid = await this.requireValidAuthenticatedSession();
    if (!isValid) {
      return false;
    }

    try {
      const payload = {
        title: event.title || '',
        description: event.description || '',
        eventStartTime: event.eventStartTime || event.eventDate || '',
        eventEndTime: event.eventEndTime || '',
        hostEmail: event.hostEmail || event.rawEvent?.hostEmail || event.rawEvent?.hostEmailAddress || '',
        imageUrl: event.imageUrl || '',
        eventLink: event.eventLink || this.getCurrentRequestUrl(),
        timezone: event.timezone || this.getLocalTimeZone(),
      };

      await firstValueFrom(
        this.http.post(
          `${environment.API_BASE_URL}user/events/${event.eventId}/join`,
          payload
        )
      );

      this.toastr.showToast('You have joined the event successfully.');
      this.pendingEvent = null;
      this.joinResumeContext.set(null);
      return true;
    } catch (error) {
      console.error('Join event failed:', error);
      // Detect already-joined: HTTP 409 Conflict or specific API messages
      if (error instanceof HttpErrorResponse) {
        const status = error.status;
        const msg: string = (error.error?.message ?? error.message ?? '').toLowerCase();
        if (status === 409 || msg.includes('already') || msg.includes('joined')) {
          this.toastr.showToast('You have already joined this event.', 'warning');
          return 'already_joined';
        }
      }
      this.toastr.showToast('Unable to join the event. Please try again.', 'error');
      return false;
    }
  }

  consumeJoinResumeContext(): JoinResumeContext | null {
    const context = this.joinResumeContext();
    this.joinResumeContext.set(null);
    return context;
  }

  private async requestConnect(walletAddress: string): Promise<ConnectResponse> {
    return firstValueFrom(
      this.http.post<ConnectResponse>(`${environment.API_BASE_URL}auth/connect`, {
        walletAddress,
      }, {
        headers: this.withCsrfHeader(),
      })
    );
  }

  private isVerifiedConnectResponse(connectResponse: ConnectResponse): boolean {
    if (connectResponse.data === true) {
      return true;
    }

    if (connectResponse.data && typeof connectResponse.data === 'object') {
      const data = connectResponse.data as {
        exists?: boolean;
        verified?: boolean;
        isVerified?: boolean;
      };

      return !!(data.exists || data.verified || data.isVerified);
    }

    return false;
  }

  private async requestNonce(walletAddress: string): Promise<{ message: string; token: string }> {
    const nonceResponse: any = await firstValueFrom(
      this.http.post(`${environment.API_BASE_URL}auth/nonce`, {
        walletAddress,
      }, {
        headers: this.withCsrfHeader(),
      })
    );

    const payload = nonceResponse?.data ?? nonceResponse;
    const message = payload?.message;
    const token = payload?.token;

    if (!message || !token) {
      throw new Error('Nonce response is missing required data.');
    }

    return { message, token };
  }

  private markAuthenticatedSession(token: string) {
    this.localStorageService.setToken(token);
  }

  private async navigateToPendingRoute(): Promise<boolean> {
    const route = this.pendingRoute;

    try {
      await this.appkitService.ready();
      await this.appkitService.updateWalletInfo();

      if (route) {
        return await this.ngZone.run(() => this.router.navigateByUrl(route));
      }
      return true;
    } catch (error) {
      console.error('Navigation to contribution page failed:', error);
      return false;
    }
  }

  /**
   * Checks if the current session is valid for performing authenticated actions.
   * Validates that the connected wallet matches the authenticated wallet and that a token exists.
   * @returns true if the session is valid, false otherwise.
   */
  canProceedWithAuthenticatedAction(): boolean {
    const token = this.localStorageService.getToken();
    const connectedWallet = this.appkitService.getWalletSignal()()?.account ?? null;
    const authenticatedWallet = this.localStorageService.getAuthenticatedWallet();
    if (!token || !connectedWallet || !authenticatedWallet) {
      return false;
    }
    return authenticatedWallet.toLowerCase() === connectedWallet.toLowerCase();
  }

  /**
   * Requires a valid authenticated session for performing authenticated actions.
   * Validates the current wallet session against the authenticated wallet and token.
   * If the session is invalid, it forces session invalidation.
   * @returns Promise that resolves to true if the session is valid, false otherwise.
   */
  async requireValidAuthenticatedSession(): Promise<boolean> {
    if (!this.isValidAuthenticatedSession()) {
      const connectedWallet = this.appkitService.getWalletSignal()()?.account ?? null;
      const isConnected = this.appkitService.getWalletSignal()()?.connected ?? false;
      const authenticatedWallet = this.localStorageService.getAuthenticatedWallet();

      if (isConnected && connectedWallet && authenticatedWallet && connectedWallet.toLowerCase() !== authenticatedWallet.toLowerCase()) {
        await this.forceSessionInvalidation();
      }
      return false;
    }

    return true;
  }

  /**
   * Disconnect the wallet without invalidating the session.
   * This is useful for scenarios where you want to disconnect the wallet but keep the session intact.  
   */
  async disconnectWalletOnly(): Promise<void> {
    this.closeModal();
    this.flowSessionId += 1;
    this.authInFlight = false;
    this.pendingEvent = null;
    this.joinResumeContext.set(null);
    this.state.update((current) => ({
      ...current,
      open: false,
      loading: false,
      flow: null,
      walletAddress: '',
      username: '',
      email: '',
      title: 'Connect wallet',
      message: 'Connect your wallet to continue.',
    }));

    await this.appkitService.disconnectWalletOnly();
  }

  async recordPurchase(payload: any): Promise<any> {
    try {
      return await firstValueFrom(
        this.http.post(`${environment.API_BASE_URL}user/account`, payload, {
          headers: this.withCsrfHeader(),
        })
      );
    } catch (error) {
      console.error('Failed to record purchase:', error);
      throw error;
    }
  }

  async getTransactionHistory(page: number = 1, limit: number = 10): Promise<any> {
    try {
      return await firstValueFrom(
        this.http.get(`${environment.API_BASE_URL}user/account/transactions?page=${page}&limit=${limit}`, {
          headers: this.withCsrfHeader(),
        })
      );
    } catch (error) {
      console.error('Failed to fetch transaction history:', error);
      throw error;
    }
  }

  buyAqlx() {
    this.startFlow('connect', '/app');
  }
}